/**
 * The arsenal.
 *
 * Four hundred and fifty guns, and not one of them written out by hand.
 *
 * The models come from a library of low-poly weapons, measured offline into
 * `weapons.manifest.json`: what each one is, how long it is, which way it
 * points. This turns that table into weapons — a class profile for the numbers
 * that make a gun feel like its kind, and a seeded wobble per weapon so no two
 * of the same class are quite the same. It is deterministic: the same model
 * always produces the same gun, so a card that says "bolt actions chamber
 * instantly" means the same thing in every run.
 *
 * Every gun carries tags — class, action, nation, era — and the upgrade cards
 * are written against those tags rather than against individual guns. That is
 * what makes the card pool deep: one card about weapons of one country is a
 * different bet from a card about one class, and with a library this size you
 * genuinely do not know which of them you will be holding.
 *
 * The guns are called what they are. The model is a Sterling, so the gun is a
 * Sterling — renaming it while its own silhouette is on screen only makes the
 * game look like it is pretending. The nations are a tag, read off the name,
 * and the numbers are chosen to feel distinct at 60 fps rather than to be
 * accurate to anything. The models are somebody else's work and are credited
 * as such: every model's author, licence and source page travel with it in the
 * manifest, and the credits screen reads them from there.
 */

import { GUNS, type GunEntry } from './gunpose.ts';

export type WeaponClass =
  | 'pistol' | 'revolver' | 'machine_pistol' | 'smg' | 'pdw' | 'shotgun'
  | 'carbine' | 'rifle' | 'battle' | 'dmr' | 'sniper' | 'antimat'
  | 'lmg' | 'launcher' | 'bow' | 'special';
export type WeaponAction = 'semi' | 'auto' | 'burst' | 'pump' | 'bolt' | 'lever' | 'charge' | 'breech';
export type WeaponEra = 'antique' | 'wartime' | 'cold' | 'modern' | 'prototype';
/**
 * Where the design comes from. Cards key off this as they do off class or era.
 *
 * Lineages rather than countries, because that is what the library actually
 * separates into: a QBZ is not Russian but it is unmistakably out of the same
 * tradition as an AK, and a Galil is an Israeli rifle built on a Finnish copy
 * of one. Six buckets that a player can feel the difference between is worth
 * more than twenty that are individually correct.
 */
export type WeaponOrigin = 'eastern' | 'german' | 'continental' | 'mediterranean' | 'anglo' | 'unmarked';

export interface Weapon {
  id: string;
  name: string;
  blurb: string;
  cls: WeaponClass;
  action: WeaponAction;
  era: WeaponEra;
  origin: WeaponOrigin;
  /** Damage per projectile. */
  damage: number;
  /** Projectiles per shot. Shotguns fire many. */
  pellets: number;
  /** Seconds between shots. */
  interval: number;
  magazine: number;
  reload: number;
  /** Cone half-angle in radians at the muzzle. */
  spread: number;
  range: number;
  /** Multiplier applied to a hit in the upper part of the body. */
  headshot: number;
  /** How hard the view kicks, in radians per shot. */
  recoil: number;
  /**
   * How far the climb wanders sideways, in radians per shot. Signed.
   *
   * The sign and the size are the gun's own and never change, so the pattern
   * is something you learn once and then own — the same reason the vertical
   * climb is a curve rather than a dice roll. It is also most of what makes
   * two rifles with similar numbers feel like different guns.
   */
  sway: number;
  /**
   * How much the cone opens over a sustained burst, as a multiple.
   *
   * 0 means the twentieth round goes exactly where the first one did. This is
   * the difference between a weapon you hold down and one you tap.
   */
  bloom: number;
  /** Rounds per pull of the trigger. Three on a burst gun, one on everything else. */
  burst: number;
  /** Reserve ammo granted when you pick it up. */
  pickupAmmo: number;
  /** Rounds this weapon punches through. 0 means it stops at the first body. */
  pierce: number;
  colour: number;
  /** Baked model file, under the weapons directory. */
  model?: string;
  /** Real length in metres — measured, not guessed. */
  modelLength?: number;
  /** The measured pose, so nothing downstream has to look it up again. */
  entry?: GunEntry;
}

// -----------------------------------------------------------------------------
// What each kind of gun is like.

interface Profile {
  damage: number; pellets: number; interval: number; magazine: number;
  reload: number; spread: number; range: number; headshot: number;
  recoil: number; ammo: number; pierce: number; action: WeaponAction;
  colour: number;
  /** Sideways drift and cone growth under sustained fire. See `Weapon`. */
  sway: number; bloom: number;
}

const PROFILES: Record<WeaponClass, Profile> = {
  pistol:        { damage: 26, pellets: 1, interval: 0.155, magazine: 15, reload: 1.05, spread: 0.010, range: 90, headshot: 2.2, recoil: 0.012, ammo: 65, pierce: 0, action: 'semi', colour: 0xb9c2cc, sway: 0.10, bloom: 0.15 },
  revolver:      { damage: 52, pellets: 1, interval: 0.34, magazine: 6, reload: 2.3, spread: 0.006, range: 110, headshot: 2.7, recoil: 0.040, ammo: 30, pierce: 1, action: 'semi', colour: 0x9a7d5a, sway: 0.22, bloom: 0.10 },
  machine_pistol:{ damage: 13, pellets: 1, interval: 0.048, magazine: 30, reload: 1.15, spread: 0.048, range: 40, headshot: 1.6, recoil: 0.010, ammo: 140, pierce: 0, action: 'auto', colour: 0xc8b06a, sway: 0.95, bloom: 1.30 },
  smg:           { damage: 18, pellets: 1, interval: 0.072, magazine: 32, reload: 1.35, spread: 0.028, range: 55, headshot: 1.7, recoil: 0.011, ammo: 140, pierce: 0, action: 'auto', colour: 0x8d949c, sway: 0.62, bloom: 0.85 },
  pdw:           { damage: 16, pellets: 1, interval: 0.055, magazine: 40, reload: 1.3, spread: 0.020, range: 72, headshot: 1.9, recoil: 0.009, ammo: 165, pierce: 1, action: 'auto', colour: 0x7f8fa0, sway: 0.34, bloom: 0.55 },
  shotgun:       { damage: 12, pellets: 9, interval: 0.72, magazine: 6, reload: 2.2, spread: 0.085, range: 28, headshot: 1.4, recoil: 0.046, ammo: 31, pierce: 1, action: 'pump', colour: 0x6f5a3e, sway: 0.30, bloom: 0.0 },
  carbine:       { damage: 24, pellets: 1, interval: 0.092, magazine: 30, reload: 1.5, spread: 0.015, range: 95, headshot: 2.0, recoil: 0.014, ammo: 120, pierce: 0, action: 'auto', colour: 0x6e7a63, sway: 0.40, bloom: 0.45 },
  rifle:         { damage: 28, pellets: 1, interval: 0.104, magazine: 30, reload: 1.65, spread: 0.012, range: 118, headshot: 2.0, recoil: 0.017, ammo: 110, pierce: 0, action: 'auto', colour: 0x6e7a63, sway: 0.28, bloom: 0.35 },
  battle:        { damage: 41, pellets: 1, interval: 0.150, magazine: 20, reload: 1.9, spread: 0.009, range: 140, headshot: 2.2, recoil: 0.029, ammo: 85, pierce: 1, action: 'auto', colour: 0x7a6a52, sway: 0.85, bloom: 0.70 },
  dmr:           { damage: 60, pellets: 1, interval: 0.30, magazine: 12, reload: 2.0, spread: 0.002, range: 185, headshot: 2.5, recoil: 0.040, ammo: 48, pierce: 1, action: 'semi', colour: 0x5f6f7a, sway: 0.18, bloom: 0.20 },
  sniper:        { damage: 115, pellets: 1, interval: 1.15, magazine: 5, reload: 2.6, spread: 0.0008, range: 220, headshot: 2.9, recoil: 0.062, ammo: 22, pierce: 2, action: 'bolt', colour: 0x6b5a3c, sway: 0.0, bloom: 0.0 },
  antimat:       { damage: 180, pellets: 1, interval: 1.60, magazine: 5, reload: 3.4, spread: 0.0008, range: 260, headshot: 2.2, recoil: 0.100, ammo: 14, pierce: 5, action: 'bolt', colour: 0x55504a, sway: 0.0, bloom: 0.0 },
  lmg:           { damage: 23, pellets: 1, interval: 0.074, magazine: 100, reload: 4.4, spread: 0.032, range: 108, headshot: 1.7, recoil: 0.020, ammo: 220, pierce: 1, action: 'auto', colour: 0x5c6350, sway: 1.20, bloom: 1.10 },
  launcher:      { damage: 74, pellets: 1, interval: 0.95, magazine: 4, reload: 2.8, spread: 0.005, range: 90, headshot: 1.0, recoil: 0.052, ammo: 12, pierce: 0, action: 'breech', colour: 0x7f7350, sway: 0.0, bloom: 0.0 },
  bow:           { damage: 98, pellets: 1, interval: 0.95, magazine: 1, reload: 1.1, spread: 0.002, range: 140, headshot: 2.8, recoil: 0.020, ammo: 20, pierce: 3, action: 'breech', colour: 0x6a7a5c, sway: 0.0, bloom: 0.0 },
  special:       { damage: 62, pellets: 1, interval: 0.55, magazine: 10, reload: 2.0, spread: 0.003, range: 165, headshot: 2.2, recoil: 0.030, ammo: 39, pierce: 3, action: 'charge', colour: 0x9fd8ff, sway: 0.12, bloom: 0.10 },
};

/** Manifest classes that are not weapons you hold. */
const NOT_A_GUN = new Set(['grenade']);

// -----------------------------------------------------------------------------
// Reading the tags off the real weapon's name.

const ORIGINS: [RegExp, WeaponOrigin][] = [
  [/\bak\b|\bak-|\bakm|\baks|\bpp-|\bppsh|\bpps|\bppd|\bsvd|dragunov|\brpk\b|\brpd\b|\bpkm?\b|\bots-|\btkb|\baek|\ba-?54|\ba-?91\b|\ban-?94|\bas val\b|\bvss\b|\bvsk\b|\bvks\b|makarov|tokarev|mosin|nagant|\bsks\b|\bsvt|\bstechkin\b|\bsaiga\b|\bvepr\b|\bks-?23\b|\bkedr\b|bizon|vityaz|veresk|groza|\bgsh|\bqbz|\bqbu|\bqbs|\bqcw|\bqsz|norinco|\btype 8|chang feng|\bcs\/|\bcs-|\bwz\.|\bvz\.|\bkbsp\b|danuvia|\bamd-?65\b|zastava|\bm76\b|\bm93\b|kalashnikov|\bsr-?[12]\b|\bpb\b|\bpss\b|\brsh-|\bsvch\b|\bash 12\b|\bamb-|\bagm-|\bmsmc\b|\bkgp|samopal|\bmpi\b|\bpm-?84\b|\bsdm\b|\bak12\b|\bakb\b|\bfedorov|federov|\bmas 3|\bsnipex\b|\balligator\b|\bt-? ?rex\b|\bosv|\bpsl\b|\bm17\b|\bvog-|\bvpo-|\bbandayevsky\b|\brb-?12\b|\bpp-?9\d\b|\bots-|\bsvdk?\b|\bsvds\b/i, 'eastern'],
  [/\bhk\b|\bhk\d|heckler|\bmp5|\bmp7\b|\bmp-?40\b|\bmp ?28\b|\bg3\b|\bg36|\bg11\b|\bxm8\b|walther|luger|mauser|masuer|\bstg|sturmgewehr|steyr|glock|\bp08\b|\bp38\b|\bppk?\b|\bhaenel\b|\bwf bern\b|schmidt|\bk31\b|\bsig\b|\bsg ?5|\bmpx\b|\bmcx\b|\bp226\b|\bp365\b|\busp\b|\bucp\b|\bslb\b|\bsl[78]\b|dreyse|borchardt|\bmp9\b|\bfmg-?9\b|\bvp70\b|\baug\b|\bgewehr|\bkar98|\bk98\b|\bmg ?4|\bfg42\b|\bbergmann\b|\bhs \.50\b|\bacr\b|\bruger\b|\bbr18\b|\bg40\b|\bmp-?443\b|\bg17\b|\bq5 match\b|\bcz ?p-?\d/i, 'german'],
  [/\bfn\b|\bfal\b|\bfnc\b|\bp90\b|\bf2000\b|five-?seven|\bfnx\b|scar|famas|\bmas\b|\bmat 49\b|chauchat|lebel|berthier|manurhin|\bmr93\b|\bmab\b|\bvhs\b|\bcz ?8|\bskorpion\b|\bscorpion evo\b|\bcz-?\d|\bcz \d|\bvz\.?5|\bbren\b|\bem[12]\b|\bapc\b|\bb&t\b|\bkriss\b|\bvector\b|\bmilkor\b|\bpanzerfaust\b|\bhecate\b|\bpgm\b|\bfr-?f1\b|\bgiat\b|\bballista\b|\bbrowning\b|\bhi-?power\b|\bbdm\b|\bbda\b/i, 'continental'],
  [/beretta|chiappa|\bspas\b|\bfranchi\b|carcano|\bpm ?12\b|valtro|\bpmx\b|\barx\b|\bcx4\b|\bm1951\b|bernardelli|\btavor\b|\bgalil\b|\buzi\b|\bjericho\b|\bimi\b|\biwi\b|\bnegev\b|\bmasada\b|\bstar\b|\bz-?84\b|\bcetme\b|\bameli\b|\bsaf\b|famae|\bhusqvarna\b|\bmateba\b|\bunica\b|\bbenelli\b|\bm4 super\b|\bmossberg\b|\bmarlin\b|\bstevens\b|\bithaca\b|\bharrington\b/i, 'mediterranean'],
  [/\bcolt\b|\bm16|\bm4\b|\bm4a|\bar-?1[05]\b|\bar-?18\b|\bm14\b|\bm1a\b|\bm1911\b|\bm1 \b|\bm3 \b|remington|winchester|smith|wesson|springfield|\bsten\b|\benfield\b|\bsmle\b|webley|\bl85|\bl11[58]\b|\bl34\b|\bbar\b|\bm60\b|\bm249\b|\bm240\b|\bm134\b|barrett|\bm82|\bm99\b|\bm107\b|cheytac|mcmillan|\bsr-?25\b|\bm110\b|\bhcar\b|\bxm2|\boicw\b|\bm203\b|\baa-?12\b|\bksg\b|\bkel-?tec\b|\bkeltec\b|\brfb\b|bushmaster|\bacr\b|\bhoneybadger\b|honey badger|\bknight|\bkac\b|\bar-?57\b|\bserbu\b|\bbfg\b|\brn-?50\b|\bpauza\b|\bgrizzly\b|\bcobray\b|\bstriker\b|\bjackhammer\b|\busas\b|\bsrm\b|\bsix 12\b|\bgen 12\b|\bnoreen\b|\bjard\b|\btroy\b|\bsavage\b|\bdan wesson\b|\bkimber\b|\bmagnum research\b|\btaurus\b|\bhi ?point\b|\bfox\b|\bstolzer\b|\bsimeon\b|\brolling block\b|\bmartini\b|\bpedersen\b|\blee navy\b|\bxcr\b|\bdrd\b|\bdt \b|\bmdr\b|\bsrs\b|\bthorneycroft\b|\bross\b|\bp14\b|\bde lisle\b|\bwelrod\b|\bboys\b|\blewis\b|\bvickers\b|\bmaxim\b|\bt-?gewehr\b|\bliberator\b|\bgyrojet\b|\bairforce\b|\btexan\b|\bpioneer\b|\bpneu-?dart\b|\bcompound\b|\bl1\d\dа?\d?\b|\bl118\b|\bm240\b|\bm249\b|\bgriffin\b|\bleader \.50\b|\brobinson\b|\btimberwolf\b|\bpgw\b|\bbeowulf\b|\brm277\b|\bfd338\b/i, 'anglo'],
];

const ERAS: [RegExp, WeaponEra][] = [
  [/\b1[6-8]\d\d\b|\b189\d\b|rolling block|martini|needle rifle|single-?action|peacemaker|\bvetterli\b|\bkropatschek\b|\bschwarzlose\b|\bborchardt\b|\bwerder\b|\byellow boy\b|\b1866\b|\b1873\b|\b1887\b|\b1894\b|\b1895\b|\b1897\b|\bmodel 1826\b/i, 'antique'],
  [/\b19[0-4]\d\b|\bm19[0-4]\d|kar98|\bk98\b|mosin|arisaka|carcano|\bsmle\b|\bp14\b|thompson|\bmp-?40\b|\bmp ?28\b|\bsten\b|\bppsh\b|\bpps-?43\b|\bppd\b|\bbar\b|\bbren\b|\bdp-?28\b|chauchat|lewis|maxim|vickers|\bstg ?44\b|\bfg42\b|\bmg ?42\b|\bmg ?34\b|luger|\bp08\b|\bp38\b|mauser|masuer|nambu|\btokarev\b|\bwelrod\b|\bde lisle\b|\bliberator\b|\bboys\b|\bt-?gewehr\b|\bowen\b|\bausten\b|\bhusqvarna\b|\bm1 garand\b|\bm1 carbine\b|\bm3 grease\b|\bm1911\b|\bfrommer\b|\bdreyse\b|\bsvt-?40\b|\bgewehr 4[13]\b|\bmas 3[68]\b|\bberthier\b|\blebel\b|\bfederov\b|\bfedorov\b|\bdanuvia\b|\bmas 38\b|\bkbsp\b|\bwz\.?3[0-9]\b|\bgeneral liu\b|\bpedersen\b|\bross\b|\bthorneycroft\b|\bschmidt\b|\bk31\b|\bm1895\b/i, 'wartime'],
  [/\bg11\b|\bxm8\b|\bxm2\d\b|\boicw\b|\btkb\b|\bacr\b|\bem[12]\b|\bthorpe\b|\bperun\b|\blu\d{3}\b|\baxl-|\bxr-?86\b|monolith|\bagm-|\bafi\b|\bpulse rifle\b|\bsawfly\b|silicone|super soaker|\bcaws\b|\bg2\b|\bpaw-?20\b|\bpar-?1\b|\bkolibri\b|\btao\b|\bcbj\b|\bbeano\b|\bgyrojet\b|\bcobray\b|\bhac-?7\b|\bmkr\b|\brm277\b|\bgriffin\b|\bpasam\b|\bapc-|\bkorsac\b/i, 'prototype'],
  [/\b19[5-8]\d\b|\bak-?47\b|\bakm\b|\bak-?74\b|\bfal\b|\bg3\b|\bm14\b|\bm16\b|\bm60\b|\buzi\b|\bmp5|\bskorpion\b|\bvz\.?6[01]\b|\brpd\b|\brpk\b|\bpkm?\b|\bsvd\b|\bdragunov\b|\bsks\b|\bstoner\b|\bar-?18\b|\bsterling\b|\bl34\b|\bmadsen\b|\bmat 49\b|\bpm ?12\b|\bcetme\b|\bvz\.?58\b|\bmac-?10\b|\bingram\b|\bbizon\b|\bkedr\b|\bas val\b|\bvss\b|\bgalil\b|\bfamas\b|\baug\b|\bl85\b|\bsig ?54\b|\bpsg\b|\bm3a1\b|\bm1951\b|\bp7\b|\bcz-?75\b|\bbulldog\b|\bmini uzi\b|\bmicro uzi\b|\bstriker\b|\bspas\b|\bm37\b|\b870\b|\bmodel 520\b|\bauto 5\b|\bks-?23\b|\bcolt scamp\b|\bpb\b|\bpss\b|\baek-?9\b|\bamd-?65\b|\bberetta 9\b|\bm1918\b/i, 'cold'],
];

const ACTIONS: [RegExp, WeaponAction][] = [
  [/\bwinchester 18[6-9]\d\b|\byellow boy\b|\b1894\b|\bmarlin model 3\b|\blever\b|\bhenry\b/i, 'lever'],
  [/kar98|\bk98\b|mosin|arisaka|carcano|\bsmle\b|\bp14\b|\bk31\b|schmidt|\bm24\b|\bm40\b|\bl96\b|\bl115\b|\bl118\b|\bawp\b|\btrg\b|\bm200\b|cheytac|mcmillan|\bax\b|\bhecate\b|\bsv-?98\b|\bwa2000\b|\bdsr\b|blaser|scout|\brpa\b|timberwolf|\bcs5\b|savage|patriot|\bvetterli\b|\bkropatschek\b|\bberthier\b|\blebel\b|\bmartini\b|rolling block|\bm1891\b|\bm1895\b|needle rifle|\bmas 36\b|\bross\b|\bthorneycroft\b|\bpedersen\b|\bgewehr 88\b|\btype 99\b|\bm1903\b|\bspringfield 187\b|\bt-?gewehr\b|\bboys\b|\bsnipex\b|\balligator\b|\bt-? ?rex\b|\bserbu\b|\brn-?50\b|\bbfg-?50\b|\btruvelo\b|\bcms\b|\bhs \.50\b|\bnoreen\b|\bjard\b|\banzio\b|\block\b/i, 'bolt'],
  [/\bm37\b|\bithaca\b|\b870\b|\b590\b|\bm500\b|\bmodel 520\b|\bwinchester model 1897\b|\bwinchester 1887\b|\bksg\b|\bks-?23\b|\buts-?15\b|\bmossberg\b|\bpump\b|\bm26 mass\b|\btoz\b|\bm203\b|\bmilkor\b|\bmgl\b/i, 'pump'],
  [/double eagle|sterlingworth|double deuce|\bcoach\b|\bside-?by-?side\b|\bover-?under\b|\bdrake\b|\bfox\b|\bderringer\b|\bpanzerfaust\b|\brpg\b|\bxm25\b|\bvog\b|\bkastet\b|\bairbow\b|\bcrossbow\b/i, 'breech'],
  [/\bburst\b|\b93r\b|\bfamas\b|\ban-?94\b|\bm16a\d\b|\bg36\b|\baug\b|\bqbz\b|\btype 95\b|\bvhs\b|\bf2000\b|\bmsbs\b|\bsar-?21\b|\bxm8\b|\baek-?97\b/i, 'burst'],
  [/\bg11\b|\bxr-?86\b|\brail\b|\barc\b|\bgyrojet\b|\bcaws\b|\bmonolith\b|\bperun\b|\bsawfly\b/i, 'charge'],
];

/**
 * What a gun is, when its shape does not say.
 *
 * The offline pass classes each model by its name and then sanity-checks that
 * against its measured proportions, which is right far more often than it is
 * wrong — but a stubby model with a folded stock measures like a pistol, and
 * that is how the MP5K ended up a semi-automatic sidearm. Anything that lands
 * in the wrong family is named here, and this wins.
 *
 * The names on screen are the real ones now, so a mis-class is not a small
 * cosmetic slip: it is a gun everybody recognises behaving like something it
 * plainly is not.
 */
const CLASSES: [RegExp, WeaponClass][] = [
  [/\bmicro uzi\b|\bscamp\b|\bmdr-?c\b|\bpar-?1\b|\bemperor scorpion\b|\baek-?919\b/i, 'machine_pistol'],
  [/\bmp5k\b|\bmp5\b|\bmp5sd\b|\bmp7\b|\bmp9\b|\bmpx\b|\bsterling\b|\bvz\.?26\b|\bsamopal\b|\bpp-?9[13]\b|\bkedr\b|\buzi\b|\bmodel 6\b|\bcolt 9mm\b|\bczw\b|\bagm-?1\b|\baek-?918\b|\bpm12\b|\bdanuvia\b/i, 'smg'],
  [/\bpb\b|\broth-?steyr\b|\bmodel 1826\b|\bzeliska\b/i, 'pistol'],
  [/\bppc\b|\bpfeifer\b/i, 'revolver'],
  [/\bsuper goose\b|\bdouble deuce\b|\bsaiga\b|\bung-?12\b|\brb-?12\b|\buts-?15\b|\b520a\b/i, 'shotgun'],
  // Semi-automatic marksman rifles, every one of which was a bolt action.
  [/\bsvd|dragunov|\bqbu\b|\bqbu-|\bsks\b|\bac556\b|\bmas 49\b|\bvpo\b|\bfd338\b|\bsr-?25\b|\bhk417\b|\btroy par\b/i, 'dmr'],
  [/\bpulse rifle\b|\bak-?12|\bakb\b|\ba-?545\b|\bscar\b|\bmcx\b|\bhk416\b|\bc7a2\b|\bqbz\b|\bvhs\b|\bxm-?29\b|\boicw\b|\bm17\b|\baxl\b|\bak-?109\b/i, 'rifle'],
  [/\bminigun\b|\bm240\b|\brpk\b|\bm1918\b|\bnegev\b|\bm249\b|\bpkm?\b/i, 'lmg'],
  [/\bsilicone\b|\bsuper soaker\b|\bsawfly\b|\bmonolith\b|\bperun\b|\bxr-?86\b|\bexpress \.177\b/i, 'special'],
];

// -----------------------------------------------------------------------------
// Names.

const LINEAGE_WORD: Record<WeaponOrigin, string> = {
  continental: 'Continental', german: 'German', eastern: 'Eastern',
  mediterranean: 'Mediterranean', anglo: 'Anglo', unmarked: 'Unmarked',
};

const NOUNS: Record<WeaponClass, string[]> = {
  pistol: ['Aide', 'Clerk', 'Magpie', 'Whisper', 'Constable', 'Notary', 'Sparrow', 'Quill', 'Latch', 'Errand', 'Second', 'Pocket', 'Understudy', 'Footnote', 'Sidearm', 'Marginal'],
  revolver: ['Verdict', 'Assize', 'Circuit', 'Sixth', 'Wheel', 'Sentence', 'Recess', 'Session', 'Bailiff', 'Docket'],
  machine_pistol: ['Wasp', 'Fidget', 'Stammer', 'Spendthrift', 'Hurry', 'Impulse', 'Tantrum', 'Blurt', 'Splutter'],
  smg: ['Chatter', 'Rattle', 'Clatter', 'Hornet', 'Spindle', 'Skitter', 'Gossip', 'Prattle', 'Static', 'Stutter', 'Shudder', 'Quibble'],
  pdw: ['Aiguille', 'Splinter', 'Tack', 'Kestrel', 'Pip', 'Shiv', 'Awl', 'Bodkin'],
  shotgun: ['Sledge', 'Squall', 'Broadside', 'Thresher', 'Gate', 'Argument', 'Fistful', 'Slam', 'Hearth', 'Gable', 'Coachgun', 'Farewell'],
  carbine: ['Shortline', 'Runner', 'Cutdown', 'Pony', 'Errand', 'Shuttle', 'Postbag', 'Nightbus'],
  rifle: ['Line Rifle', 'Standard', 'Column', 'Regular', 'Pattern', 'Service', 'Ledger', 'Roster', 'Common', 'Currency', 'Ordinary', 'Register'],
  battle: ['Longarm', 'Anvil', 'Girder', 'Rampart', 'Bulwark', 'Buttress', 'Keystone', 'Lintel'],
  dmr: ['Vantage', 'Overlook', 'Reach', 'Auditor', 'Surveyor', 'Assessor', 'Prospect', 'Landing'],
  sniper: ['Bolt', 'Full Stop', 'Distance', 'Vigil', 'Comet', 'Nocturne', 'Postscript', 'Epilogue', 'Envoi', 'Colophon'],
  antimat: ['Hammer', 'Breaker', 'Cathedral', 'Foundry', 'Tectonic', 'Viaduct', 'Caisson', 'Pile Driver'],
  lmg: ['Drum', 'Belt', 'Downpour', 'Weather', 'Monsoon', 'Threshing', 'Turbine', 'Millrace'],
  launcher: ['Thumper', 'Postbox', 'Parcel', 'Lob', 'Delivery', 'Consignment', 'Hamper'],
  bow: ['Silence', 'Yew', 'Whisperer', 'Hush', 'Sotto Voce'],
  special: ['Rail', 'Arc', 'Anomaly', 'Errata', 'Conjecture', 'Hypothesis', 'Footnote'],
};

const BLURBS: Record<WeaponClass, string[]> = {
  pistol: ['Standard issue. Never jams, never impresses.', 'What you reach for when the plan has already failed.', 'Fourteen chances to change your mind.', 'Small, honest, and out of its depth by floor five.'],
  revolver: ['Six, then a long and public reload.', 'Loud enough to be an argument all by itself.', 'No magazine to lose, no safety to forget.', 'Every round is a decision you have to live with.'],
  machine_pistol: ['Empties itself before you have decided anything.', 'A pistol that has heard about automatic fire.', 'Cheerfully ungovernable.', 'All of it, everywhere, immediately.'],
  smg: ['Stamped from sheet metal in a hurry, and it shows.', 'Fast, light, and utterly unbothered about where the rounds go.', 'Made in a bicycle factory, apparently.', 'Sprays like a garden hose with opinions.'],
  pdw: ['Small round, small gun, surprising hole.', 'Punches through what it has no business punching through.', 'Issued to people who drive for a living.', 'Quicker than it is loud.'],
  shotgun: ['Slow, heavy, and it ends conversations.', 'Two barrels, no argument.', 'A shotgun that forgot it was supposed to pause.', 'Fine at nine metres. Decorative at thirty.'],
  carbine: ['A rifle that has been to the hacksaw.', 'Short enough for stairwells.', 'Everything the rifle had, minus the reach.', 'Handles like something half its calibre.'],
  rifle: ['What everyone was issued, and what everyone kept.', 'Boring in the way that keeps you alive.', 'Thirty rounds and no surprises.', 'The gun the manual was written about.'],
  battle: ['Heavier than it needs to be. Hits like it.', 'Full-power rounds and a shoulder that knows it.', 'Built before anyone worried about weight.', 'Recoils like it resents you.'],
  dmr: ['Semi-automatic and quietly rude about it.', 'Patient. Expensive. Correct.', 'For when the room is longer than the room should be.', 'Rewards the shot you take, not the shots you take.'],
  sniper: ['One round, one answer, then a long think about it.', 'Bring it to a corridor and regret everything.', 'Puts a full stop at the end of the hallway.', 'The scope is optimism; the round is not.'],
  antimat: ['Designed for engine blocks. Used otherwise.', 'The wall behind them is also a target.', 'Shoulders were not part of the design brief.', 'Anything in the line gets a share.'],
  lmg: ['A hundred rounds and no plan for any of them.', 'Belt-fed, bipod-less, deeply unreasonable.', 'Reloading takes long enough to write home.', 'Suppression, in the sense of weather.'],
  launcher: ['Lobs a small argument over the nearest crate.', 'Never fire it at something you are standing next to.', 'Arcs beautifully. Lands unkindly.', 'Comes with a strong recommendation about distance.'],
  bow: ['Silent, patient, and entirely out of period.', 'No muzzle flash to give you away.', 'Draws slowly. Arrives quietly.', 'Nothing to reload, everything to retrieve.'],
  special: ['Nobody will say who built it. It goes through walls.', 'Fires something. It is not strictly a bullet.', 'The manual is one page and mostly warnings.', 'Prototype: read as "nobody signed it off".'],
};

// -----------------------------------------------------------------------------

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic, seeded per gun: the same model is always the same weapon. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function match<T>(rules: [RegExp, T][], name: string, fallback: T): T {
  for (const [re, v] of rules) if (re.test(name)) return v;
  return fallback;
}

/** Slug from the model uid, so ids survive the library growing. */
function idOf(e: GunEntry): string {
  return `w_${e.uid}`;
}

const ROMAN = ['', ' Mk II', ' Mk III', ' Mk IV', ' Mk V', ' Mk VI', ' Mk VII', ' Mk VIII', ' Mk IX', ' Mk X'];

function build(): Weapon[] {
  const used = new Set<string>();
  const out: Weapon[] = [];
  const pool = GUNS.filter((e) => e.ok && !NOT_A_GUN.has(e.cls));
  // Sorted, so the names a run hands out do not depend on directory order.
  pool.sort((a, b) => a.uid.localeCompare(b.uid));

  for (const e of pool) {
    const bare = e.title.replace(/^low-?poly\s*/i, '').trim();
    const measured = (PROFILES[e.cls as WeaponClass] ? e.cls : 'special') as WeaponClass;
    const cls = match(CLASSES, bare, measured);
    const p = PROFILES[cls];
    const r = rng(hash(e.uid));

    const origin = match(ORIGINS, bare, 'unmarked');
    const era = match(ERAS, bare, cls === 'special' ? 'prototype' : 'modern');
    let action = match(ACTIONS, bare, p.action);
    // A class and an action have to agree: a pump-action pistol is a typo.
    if (cls === 'pistol' || cls === 'revolver') action = action === 'auto' ? 'auto' : 'semi';
    if (cls === 'lmg' || cls === 'machine_pistol') action = 'auto';

    // Up to ±18%, and the wobble is correlated: a gun that hits harder fires
    // slower, so nothing comes out strictly better than its whole class.
    const punch = 0.82 + r() * 0.36;
    const spray = 0.85 + r() * 0.3;
    const hold = 0.85 + r() * 0.3;
    const slow = 1 + (punch - 1) * 0.55;

    /*
     * Its own name.
     *
     * Two models of the same weapon get a mark to tell them apart, and a model
     * that arrived without a usable title falls back to the invented pool —
     * which is the only thing that pool is still for.
     */
    let name = bare;
    if (!name || name.length > 34) {
      const nouns = NOUNS[cls];
      name = `${LINEAGE_WORD[origin]} ${nouns[(hash(e.uid) >>> 0) % nouns.length]}`;
    }
    if (used.has(name)) {
      const stem = name;
      for (let i = 1; i < ROMAN.length && used.has(name); i++) name = stem + ROMAN[i];
    }
    used.add(name);

    const blurbs = BLURBS[cls];
    const tint = (p.colour & 0xfefefe) >> 1;
    const shade = Math.floor(r() * 0x22) * 0x010101;

    out.push({
      id: idOf(e),
      name,
      blurb: blurbs[(hash(e.uid + 'b') >>> 0) % blurbs.length],
      cls, action, era, origin,
      damage: Math.max(1, Math.round(p.damage * punch)),
      pellets: p.pellets,
      interval: +(p.interval * slow).toFixed(4),
      magazine: Math.max(1, Math.round(p.magazine * spray)),
      reload: +(p.reload * (2 - hold)).toFixed(2),
      spread: +(p.spread * (2 - spray)).toFixed(4),
      range: Math.round(p.range * hold),
      headshot: +(p.headshot * (0.94 + r() * 0.12)).toFixed(2),
      recoil: +(p.recoil * punch).toFixed(4),
      // Which way it walks is the gun's own, fixed for as long as it exists.
      sway: +(p.sway * (0.7 + r() * 0.6) * (r() < 0.5 ? -1 : 1) * 0.01).toFixed(5),
      bloom: +(p.bloom * (0.8 + r() * 0.4)).toFixed(3),
      burst: action === 'burst' ? 3 : 1,
      pickupAmmo: Math.round(p.ammo * spray),
      pierce: p.pierce,
      colour: (p.colour - tint + tint) ^ shade,
      model: e.file,
      modelLength: e.length,
      entry: e,
    });
  }
  return out;
}

export const WEAPONS: Weapon[] = build();
export const BY_ID = new Map(WEAPONS.map((w) => [w.id, w]));

/**
 * What you start every run with.
 *
 * A plain semi-automatic pistol, chosen rather than rolled: the first thirty
 * seconds of a run should teach you the controls, not the gun.
 */
export const STARTER: Weapon =
  WEAPONS.find((w) => w.cls === 'pistol' && w.action === 'semi' && w.magazine >= 12) ??
  WEAPONS.find((w) => w.cls === 'pistol') ??
  WEAPONS[0];

export function weaponById(id: string): Weapon {
  return BY_ID.get(id) ?? STARTER;
}

/** Guns that can be found on the floor — everything but what you start with. */
export const DROPPABLE = WEAPONS.filter((w) => w.id !== STARTER.id);

/**
 * Which floors a gun is allowed to turn up on.
 *
 * By class first and by numbers second, which is the opposite of how this
 * started. Sorting purely on damage-per-second put every shotgun in the game
 * behind floor six — a shotgun's burst is nine pellets and the arithmetic sees
 * a hundred and eight damage — and left the first five floors holding nothing
 * but pistols, revolvers and marksman rifles, because those are the only
 * classes whose raw figures are small. Five floors of one idea about weapons
 * is the thing the library was collected to avoid.
 *
 * A shotgun is not a late-game weapon; it is a *close range* weapon, and the
 * cost of that is paid in where you have to stand rather than in when you are
 * allowed to have it. So the class decides the band and the numbers only move
 * a gun up when it is unusually strong for its kind.
 */
const CLASS_TIER: Record<WeaponClass, number> = {
  pistol: 0, revolver: 0, smg: 0, machine_pistol: 0, pdw: 0, shotgun: 0, carbine: 0, bow: 0,
  rifle: 1, battle: 1, dmr: 1,
  lmg: 2, sniper: 2, launcher: 2,
  antimat: 3, special: 3,
};

export function tier(w: Weapon): number {
  if (w.era === 'prototype') return 3;
  const base = CLASS_TIER[w.cls] ?? 1;
  // Unusually hard-hitting for its kind: one band later than its siblings.
  const dps = (w.damage * w.pellets) / w.interval;
  const burst = w.damage * w.pellets;
  const hot = dps > 420 || burst > 165;
  return Math.min(3, base + (hot ? 1 : 0));
}

const BY_TIER: Weapon[][] = [[], [], [], []];
for (const w of DROPPABLE) BY_TIER[tier(w)].push(w);

/**
 * How often each kind of gun turns up, regardless of how many of it exist.
 *
 * The library is not a fair sample of what a shooter wants. Whoever modelled
 * it liked long guns: there are sixty-one sniper rifles and twenty-three
 * anti-materiel rifles against six carbines, so picking uniformly hands the
 * player a bolt-action optic every fifth drop — in a game largely fought at
 * eight metres in rooms twenty metres across. The result reads as the game
 * having one idea about weapons, which is the opposite of what four hundred
 * models were collected for.
 *
 * So the *class* is rolled first and the weapon second. The counts in the
 * library then decide only which particular AK you get, not how often you are
 * handed a rifle at all, and the felt variety of a run is a property of this
 * table rather than of somebody's modelling habits.
 */
const APPETITE: Record<WeaponClass, number> = {
  smg: 10, shotgun: 9, rifle: 9, carbine: 8, battle: 7, pdw: 7,
  pistol: 6, revolver: 6, machine_pistol: 5, lmg: 5, dmr: 5,
  launcher: 3, sniper: 3, antimat: 2, special: 2, bow: 1,
};

export function dropFor(depth: number, roll: number): Weapon {
  const maxTier = depth < 3 ? 0 : depth < 6 ? 1 : depth < 9 ? 2 : 3;
  // Weighted to the deepest tier available, so descending feels like an
  // escalation rather than a wider lottery.
  const bands: Weapon[][] = [];
  for (let t = 0; t <= maxTier; t++) if (BY_TIER[t].length) bands.push(BY_TIER[t]);
  if (!bands.length) return DROPPABLE[Math.floor(roll * DROPPABLE.length) % DROPPABLE.length];
  const pick = bands[Math.min(bands.length - 1, Math.floor(roll * roll * bands.length * 1.3))] ?? bands[bands.length - 1];

  /*
   * Roll a class this band actually has, then a gun of it.
   *
   * Two independent draws off the one number, which is all the caller gives
   * us: the low digits choose the class and the high ones choose the weapon.
   * A band with no shotguns in it simply never offers a shotgun rather than
   * falling through to something else, so the appetite is a preference and
   * never a promise the library cannot keep.
   */
  const kinds = new Map<WeaponClass, Weapon[]>();
  for (const w of pick) {
    const bag = kinds.get(w.cls);
    if (bag) bag.push(w);
    else kinds.set(w.cls, [w]);
  }
  let total = 0;
  for (const cls of kinds.keys()) total += APPETITE[cls] ?? 1;
  // A second, independent-looking draw off the one number the caller gave us:
  // the band was chosen by `roll` itself, so the class has to be chosen by
  // something that does not move with it or the two decisions are one.
  let want = ((Math.sin(roll * 12.9898) * 43758.5453) % 1 + 1) % 1 * total;
  for (const [cls, bag] of kinds) {
    want -= APPETITE[cls] ?? 1;
    if (want <= 0) return bag[Math.floor(roll * 9973) % bag.length];
  }
  return pick[Math.floor(roll * 9973) % pick.length];
}

// -----------------------------------------------------------------------------
// What class a gun is, at a glance.

/*
 * Sixteen classes, seven colours.
 *
 * Each class already carried a `colour`, and it was doing nothing: those are
 * finish tints for the block stand-in, they are all desaturated greys and
 * browns by design, carbine and rifle were literally the same value, and the
 * stand-in is hidden the instant the real model loads. So a weapon shipped
 * with no visible indication of what it was.
 *
 * Grouping is the point. Sixteen hues would be sixteen shades of
 * indistinguishable; seven families with a hue each are legible at a glance,
 * and the families are the thing you actually reason about when a gun drops —
 * whether it is a sidearm or a marksman rifle, not whether it is a DMR or an
 * anti-materiel rifle.
 */
export interface WeaponFamily {
  id: string;
  name: string;
  /** Saturated on purpose: it is read at thumbnail size in a dark room. */
  colour: number;
}

export const FAMILIES: Record<string, WeaponFamily> = {
  sidearm:   { id: 'sidearm',   name: 'Sidearm',  colour: 0x3d8bff },
  machine:   { id: 'machine',   name: 'Machine',  colour: 0x18d6c4 },
  scatter:   { id: 'scatter',   name: 'Scatter',  colour: 0xff6a1e },
  assault:   { id: 'assault',   name: 'Assault',  colour: 0x46d054 },
  marksman:  { id: 'marksman',  name: 'Marksman', colour: 0xa96cff },
  support:   { id: 'support',   name: 'Support',  colour: 0xff3b47 },
  exotic:    { id: 'exotic',    name: 'Exotic',   colour: 0xe8f0ff },
};

const FAMILY_OF: Record<WeaponClass, string> = {
  pistol: 'sidearm', revolver: 'sidearm', machine_pistol: 'sidearm',
  smg: 'machine', pdw: 'machine',
  shotgun: 'scatter',
  carbine: 'assault', rifle: 'assault', battle: 'assault',
  dmr: 'marksman', sniper: 'marksman', antimat: 'marksman',
  lmg: 'support', launcher: 'support',
  bow: 'exotic', special: 'exotic',
};

export function familyOf(cls: WeaponClass): WeaponFamily {
  return FAMILIES[FAMILY_OF[cls]] ?? FAMILIES.assault;
}
